import { ApplicationDataStore } from '../application/ApplicationDataStore';
import { DataItem } from '@servicetitan/financing-utils-shared';
import { extractLocation } from './extractLocation';
import startCase from 'lodash/startCase';
import { ReviewCard } from './ReviewCard';
import { ApplicantContainer } from '../applicant';
import { useRef } from 'react';
import { ImperativeForm } from '../common/Form';
import { useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { usePersonalInfoReviewFields } from './useReviewFields';

export const Applicant = () => {
    const { hasIdFields, useAnnualIncome } = useProviderConfig();
    const { applicationData } = ApplicationDataStore.useStore();
    const applicant = applicationData.applicant?.primaryApplicant;
    const formRef = useRef<ImperativeForm>(null);
    const { fieldOrder, fieldRegistry } = usePersonalInfoReviewFields();

    const idIssueLocation = extractLocation(applicant?.applicantId);

    return (
        <ReviewCard title="Applicant" formRef={formRef}>
            <ReviewCard.View>
                {/* Special handling for Name field - combine firstName and lastName */}
                <DataItem
                    label="Name"
                    text={`${applicant?.firstName ?? ''} ${applicant?.lastName ?? ''}`.trim()}
                />
                {fieldOrder.map(fieldKey => {
                    const field = fieldRegistry[fieldKey];
                    if (!field) {
                        return null;
                    }

                    // Skip firstName and lastName as they're handled above
                    if (fieldKey === 'firstName' || fieldKey === 'lastName') {
                        return null;
                    }

                    // Get review label (can be function or string)
                    const label =
                        typeof field.reviewLabel === 'function'
                            ? field.reviewLabel({ useAnnualIncome } as any)
                            : field.reviewLabel;

                    // Get review value
                    const value = field.reviewValue(applicant);
                    if (!value) {
                        return null; // Skip if no data
                    }

                    return (
                        <DataItem
                            key={fieldKey}
                            label={label}
                            text={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        />
                    );
                })}
                {hasIdFields && (
                    <>
                        <DataItem
                            label="Id Type"
                            text={startCase(applicant?.applicantId?.applicantIdDocumentType)}
                        />
                        <DataItem label="Id Number" text={applicant?.applicantId?.documentId} />
                        <DataItem label="ID Issue Date" text={applicant?.applicantId?.issueDate} />
                        <DataItem
                            label="ID Expiration Date"
                            text={applicant?.applicantId?.expirationDate}
                        />
                        <DataItem label={idIssueLocation.label} text={idIssueLocation.name} />
                    </>
                )}
            </ReviewCard.View>
            <ReviewCard.Edit>
                <ApplicantContainer formRef={formRef} />
            </ReviewCard.Edit>
        </ReviewCard>
    );
};
